"use client";

import React, { useMemo, useRef, useEffect, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  CornerDownLeft,
  Search,
  Variable,
} from "lucide-react";
import type { NodeOption } from "@/components/SpotflowV2/Sidebar/variableOptions";
import { DataTypeBadge } from "@/icons/DataTypeIcon";
import ImageWithFallback from "@/components/ImageWithFallback";
import { NoxusIcon } from "@/components/UI/NoxusIcon";
import { VariableScopeBadge } from "./VariableScopeBadge";
import { Kbd } from "@/components/shadcn/kbd";
import { TruncatedTextWithTooltip } from "@/components/TruncatedTextWithTooltip";
import { cn } from "@/lib/utils";

export interface InlineVariableInsert {
  value: string;
  label: string;
  nodeName: string;
  nodeColor: string;
  nodeImage?: string;
}

export function filterVariableOptions(
  options: NodeOption[],
  search: string
): NodeOption[] {
  if (!search.trim()) return options;
  const q = search.toLowerCase();
  return options
    .map((node) => ({
      ...node,
      outputs: node.outputs.filter(
        (o) =>
          o.outputLabel.toLowerCase().includes(q) ||
          node.nodeName.toLowerCase().includes(q)
      ),
    }))
    .filter((node) => node.outputs.length > 0);
}

export interface FlatVariableItem {
  value: string;
  label: string;
  nodeName: string;
  nodeColor: string;
  nodeImage?: string | null;
  dataType: string;
  isList?: boolean;
}

export function flattenVariableOptions(
  options: NodeOption[]
): FlatVariableItem[] {
  const items: FlatVariableItem[] = [];
  for (const node of options) {
    for (const output of node.outputs) {
      items.push({
        value: output.value,
        label: output.outputLabel,
        nodeName: node.nodeName,
        nodeColor: node.nodeColor,
        nodeImage: node.nodeImage,
        dataType: output.dataType,
        isList: output.isList,
      });
    }
  }
  return items;
}

// Node icon on its tinted background — same treatment as the config panel
// header and the bindings list rows.
export function VariableSourceIcon({
  image,
  iconName,
  color,
  isSecret,
}: {
  image?: string | null;
  iconName?: string | null;
  color?: string | null;
  isSecret?: boolean;
}) {
  if (iconName) return <NoxusIcon icon={iconName} size="sm" />;
  return (
    <span
      className="flex size-6 shrink-0 items-center justify-center rounded-md"
      style={{ backgroundColor: color ?? "#f4f4f5" }}
    >
      {isSecret ? (
        <Variable size={14} strokeWidth={1.75} className="text-indigo-600" />
      ) : image ? (
        <span className="relative size-3.5">
          <ImageWithFallback
            fill
            style={{ objectFit: "contain" }}
            alt=""
            src={image}
          />
        </span>
      ) : (
        <span className="size-3 rounded bg-white/40" />
      )}
    </span>
  );
}

const FOOTER_KBD =
  "bg-transparent border border-border font-normal text-muted-foreground";

interface VariablePickerPanelProps {
  options: NodeOption[];
  search: string;
  onSearchChange?: (query: string) => void;
  showSearch?: boolean;
  activeIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  onSelect: (item: InlineVariableInsert) => void;
  /** Ref id of the currently-bound variable, if any: its row is visibly
   * marked as selected and the keyboard cursor starts on it. */
  selectedValue?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** Hover callback for canvas highlighting: called with the hovered output's
   * source, and with no arguments when the pointer leaves it. */
  onHoverOutput?: (sourceNodeId?: string, sourceOutputName?: string) => void;
  autoFocusSearch?: boolean;
  className?: string;
}

export function VariablePickerPanel({
  options,
  search,
  onSearchChange,
  showSearch = true,
  activeIndex: controlledActiveIndex,
  onActiveIndexChange,
  onSelect,
  selectedValue,
  onKeyDown,
  onHoverOutput,
  autoFocusSearch,
  className,
}: VariablePickerPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filteredOptions = useMemo(
    () => filterVariableOptions(options, search),
    [options, search]
  );

  const flatItems = useMemo(
    () => flattenVariableOptions(filteredOptions),
    [filteredOptions]
  );

  // Start the keyboard cursor on the bound variable so re-opening the picker
  // lands on the current selection.
  const [internalActiveIndex, setInternalActiveIndex] = useState(() => {
    if (!selectedValue) return 0;
    const idx = flatItems.findIndex((item) => item.value === selectedValue);
    return idx >= 0 ? idx : 0;
  });

  const activeIndex = controlledActiveIndex ?? internalActiveIndex;
  const setActiveIndex = onActiveIndexChange ?? setInternalActiveIndex;

  useEffect(() => {
    if (autoFocusSearch) {
      inputRef.current?.focus();
    }
  }, [autoFocusSearch]);

  useEffect(() => {
    const maxIndex = Math.max(flatItems.length - 1, 0);
    if (activeIndex > maxIndex) {
      setActiveIndex(maxIndex);
    }
  }, [flatItems.length, activeIndex, setActiveIndex]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-idx="${activeIndex}"]`
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(activeIndex + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(activeIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[activeIndex];
      if (item) {
        onSelect({
          value: item.value,
          label: item.label,
          nodeName: item.nodeName,
          nodeColor: item.nodeColor,
          nodeImage: item.nodeImage ?? undefined,
        });
      }
    }
  };

  let flatIdx = -1;

  return (
    <div className={cn("flex flex-col overflow-hidden", className)}>
      {showSearch && (
        <div className="flex items-center gap-2 px-3 h-9 border-b shrink-0">
          <Search size={13} className="shrink-0 text-muted-foreground/50" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => {
              onSearchChange?.(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Search variables…"
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground/50 text-foreground"
          />
        </div>
      )}

      <div ref={listRef} className="max-h-[400px] overflow-y-auto py-1">
        {filteredOptions.length === 0 && (
          <p className="py-6 text-sm text-center text-muted-foreground">
            No variables found
          </p>
        )}
        {filteredOptions.map((node) => (
          <React.Fragment key={node.nodeId}>
            <div className="flex items-center gap-2 px-3 pt-2 pb-1">
              <VariableSourceIcon
                image={node.nodeImage}
                iconName={node.nodeIconName}
                color={node.nodeColor}
                isSecret={node.nodeId === "__secrets__"}
              />
              <div className="min-w-0 text-sm text-muted-foreground">
                <TruncatedTextWithTooltip>
                  {node.nodeName}
                </TruncatedTextWithTooltip>
              </div>
            </div>

            {node.outputs.map((output) => {
              flatIdx++;
              const currentIdx = flatIdx;
              const isActive = currentIdx === activeIndex;
              const isSelected = output.value === selectedValue;
              return (
                <button
                  key={output.value}
                  type="button"
                  data-idx={currentIdx}
                  data-selected={isSelected}
                  className={cn(
                    "relative w-full flex cursor-pointer items-center justify-between gap-3 pl-11 pr-3 py-2 text-left transition-colors",
                    isSelected
                      ? isActive
                        ? "bg-blue-100/60"
                        : "bg-blue-50/60"
                      : isActive
                        ? "bg-zinc-100/60"
                        : "hover:bg-zinc-100/40"
                  )}
                  onMouseEnter={() => {
                    setActiveIndex(currentIdx);
                    onHoverOutput?.(node.nodeId, output.outputName);
                  }}
                  onMouseLeave={() => onHoverOutput?.()}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect({
                      value: output.value,
                      label: output.outputLabel,
                      nodeName: node.nodeName,
                      nodeColor: node.nodeColor,
                      nodeImage: node.nodeImage ?? undefined,
                    });
                  }}
                >
                  {isSelected && (
                    <span className="absolute left-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center">
                      <Check size={13} className="text-primary" />
                    </span>
                  )}
                  <div className="flex-1 min-w-0 text-sm text-foreground">
                    <TruncatedTextWithTooltip>
                      {output.outputLabel}
                    </TruncatedTextWithTooltip>
                  </div>
                  {output.scope && (
                    <VariableScopeBadge scope={output.scope} size="sm" />
                  )}
                  <DataTypeBadge
                    dataType={output.dataType}
                    isList={output.isList}
                    className="shrink-0"
                  />
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className="flex shrink-0 items-center gap-3 border-t px-3 py-1.5 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <Kbd className={FOOTER_KBD}>
            <ArrowUp />
          </Kbd>
          <Kbd className={FOOTER_KBD}>
            <ArrowDown />
          </Kbd>
          Navigate
        </span>
        <span className="flex items-center gap-1">
          <Kbd className={FOOTER_KBD}>
            <CornerDownLeft />
          </Kbd>
          Select
        </span>
      </div>
    </div>
  );
}
